import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatAuthError, verifySessionPersisted } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BrandMark from "../components/BrandMark";
import { SupportInline } from "../components/SupportCard";
import CookieWarningBanner from "../components/CookieWarningBanner";

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cookieBlocked, setCookieBlocked] = useState(false);
  const refCode = (searchParams.get("ref") || "").trim().toUpperCase() || null;

  // Fire a click ping the first time /register loads with a ?ref= — dedupe
  // per session with sessionStorage so a refresh doesn't inflate metrics.
  useEffect(() => {
    if (!refCode) return;
    const key = `ff_ref_click_${refCode}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post("/api/referrals/click", { ref: refCode }).catch(() => {});
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setCookieBlocked(false);
    try {
      const { data } = await api.post("/api/auth/register", {
        email, password, name, ref_code: refCode,
      });
      const persisted = await verifySessionPersisted();
      if (!persisted) {
        setCookieBlocked(true);
        return;
      }
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatAuthError(err, "Could not create your account. Please try again."));
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden" data-testid="register-page">
      <div className="ambient-orb" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-7">
          <BrandMark size={44} />
        </Link>
        {cookieBlocked && (
          <div className="mb-4">
            <CookieWarningBanner onClose={() => setCookieBlocked(false)} />
          </div>
        )}
        <div className="card-surface p-7 sm:p-8">
          <div className="label-mono">Sign up</div>
          <h1 className="text-3xl text-white font-semibold tracking-tight mt-1">Create your terminal</h1>

          <button onClick={googleSignIn} className="mt-6 w-full btn-ghost flex items-center justify-center gap-2.5" data-testid="google-signup-btn">
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#fff" d="M21.6 12.2c0-.7-.06-1.4-.18-2H12v3.78h5.4a4.62 4.62 0 0 1-2 3.04v2.5h3.23c1.89-1.74 2.97-4.3 2.97-7.32z"/><path fill="#fff" d="M12 22c2.7 0 4.96-.9 6.6-2.42l-3.23-2.5c-.9.6-2.04.95-3.37.95-2.6 0-4.8-1.75-5.58-4.1H3.06v2.58A10 10 0 0 0 12 22z"/><path fill="#fff" d="M6.42 13.93c-.2-.6-.32-1.24-.32-1.93s.11-1.33.32-1.93V7.5H3.06A10 10 0 0 0 2 12c0 1.61.39 3.14 1.06 4.5l3.36-2.57z"/><path fill="#fff" d="M12 5.96c1.47 0 2.79.5 3.83 1.5l2.86-2.86A10 10 0 0 0 12 2 10 10 0 0 0 3.06 7.5l3.36 2.57C7.2 7.7 9.4 5.96 12 5.96z"/></svg>
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px bg-white/10 flex-1"></div>
            <span className="label-mono">OR</span>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="label-mono mb-1.5 block">Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-glass" data-testid="register-name-input" />
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-glass" data-testid="register-email-input" />
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-glass" data-testid="register-password-input" />
            </div>
            {error && <div className="text-[var(--bear)] text-xs font-mono" data-testid="register-error">{error}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full mt-1" data-testid="register-submit-btn">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>
          <p className="mt-4 text-white/40 text-[11px] leading-relaxed">
            By signing up you agree this platform is for educational purposes only and is not financial advice.
          </p>
          <div className="mt-4 text-center text-sm text-white/55">
            Already have an account?{" "}
            <Link to="/login" className="text-white hover:text-[var(--bull)]" data-testid="login-link">Sign in</Link>
          </div>
        </div>
        <SupportInline className="mt-6" />
      </div>
    </div>
  );
}
